// src/stores/authStore.ts
// מנהל מצב ההתחברות - תואם לכל הקבצים בפרויקט

import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';

// טיפוס משתמש - כולל את כל השדות שהקבצים הישנים מצפים להם
export interface User {
  id: string;
  email: string;
  name: string;
  firstName?: string;
  lastName?: string;
  phone?: string;
  role: 'parent' | 'staff' | 'admin' | 'tablet' | 'sysadmin' | 'teacher';
  kindergartenPublicId?: string;
  kindergartenName?: string;
  kindergartenCode?: string;
  publicId?: string;
  token?: string;
  organizationPublicId?: string;
  organizationName?: string;
  serverRole?: string;
}

// טיפוס ה-Store
interface AuthState {
  user: User | null;
  token: string | null;
  refreshToken: string | null;
  tokenExpiresAt: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  requiresPasswordChange: boolean;
  requiresAgreement: boolean;
  agreementVersion: string | null;
  setUser: (user: User | null) => void;
  setToken: (token: string | null) => void;
  setRefreshToken: (refreshToken: string | null) => void;
  setTokenExpiresAt: (expiresAt: string | null) => void;
  isTokenExpiringSoon: () => boolean;
  setRequiresPasswordChange: (value: boolean) => void;
  setRequiresAgreement: (value: boolean, version?: string | null) => void;
  login: (email: string, password: string) => Promise<boolean>;
  logout: () => void;
  clearAuth: () => void;
  setLoading: (loading: boolean) => void;
}

// יצירת ה-Store עם persist
export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      token: null,
      refreshToken: null,
      tokenExpiresAt: null,
      isAuthenticated: false,
      isLoading: false,
      requiresPasswordChange: false,
      requiresAgreement: false,
      agreementVersion: null,

      setUser: (user) => set({
        user,
        isAuthenticated: !!user
      }),

      setToken: (token) => set({ token }),

      setRefreshToken: (refreshToken) => set({ refreshToken }),

      setTokenExpiresAt: (tokenExpiresAt) => set({ tokenExpiresAt }),

      // בדיקה אם ה-token פג או עומד לפוג בתוך 5 דקות
      isTokenExpiringSoon: () => {
        const { tokenExpiresAt } = get();
        if (!tokenExpiresAt) return true; // אין מידע על תפוגה - מניחים שפג
        const expiresAt = new Date(tokenExpiresAt).getTime();
        const now = Date.now();
        const FIVE_MINUTES = 5 * 60 * 1000;
        return expiresAt - now < FIVE_MINUTES;
      },

      setRequiresPasswordChange: (value) => set({ requiresPasswordChange: value }),

      setRequiresAgreement: (value, version = null) => set({
        requiresAgreement: value,
        agreementVersion: version ?? null,
      }),

      login: async (_email: string, _password: string) => {
        // לא בשימוש ישיר - הכניסה מתבצעת דרך authApi
        return false;
      },

      logout: () => {
        set({
          user: null,
          token: null,
          refreshToken: null,
          tokenExpiresAt: null,
          isAuthenticated: false,
          requiresPasswordChange: false,
          requiresAgreement: false,
          agreementVersion: null,
        });
      },

      // ניקוי מלא - לשימוש כשאימות נכשל לצמיתות
      clearAuth: () => {
        set({
          user: null,
          token: null,
          refreshToken: null,
          tokenExpiresAt: null,
          isAuthenticated: false,
          requiresPasswordChange: false,
          requiresAgreement: false,
          agreementVersion: null,
        });
        window.dispatchEvent(new CustomEvent('kipikid-tablet-auth-expired'));
      },

      setLoading: (loading) => set({ isLoading: loading }),
    }),
    {
      name: 'kipikid-auth',
      // sessionStorage = per-tab isolation — prevents tablet/parent/admin tabs from overwriting each other
      storage: createJSONStorage(() => sessionStorage),
      partialize: (state) => ({
        user: state.user,
        token: state.token,
        refreshToken: state.refreshToken,
        tokenExpiresAt: state.tokenExpiresAt,
        isAuthenticated: state.isAuthenticated,
        requiresPasswordChange: state.requiresPasswordChange,
        requiresAgreement: state.requiresAgreement,
        agreementVersion: state.agreementVersion,
      }),
    }
  )
);
